import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  MENU_ALLERGEN_LABELS,
  MENU_COURSE_LABELS,
  MENU_MEAL_LABELS,
  MENU_MEALS,
  type MenuMeal,
} from "@alm/domain";
import { EmptyState } from "@/components/app/lists/empty-state";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { OptionSelect, SelectOption } from "@/components/ui/option-select";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus } from "@/lib/icons";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { optionalOneOf, KEEP_PLACE, searchPatch, optionalSearch } from "@/lib/search";
import { cn } from "@/lib/utils";
import { orpc } from "@/lib/orpc";
import { keepWhileSame } from "@/lib/query/keep-while-same";
import { CommercialKitchenBar } from "./-components/commercial-kitchen-bar";
import { CourseIcon } from "./-components/course-icon";
import { DishSheet, type SheetTarget } from "./-components/dish-sheet";
import { MenusPager } from "./-components/menus-pager";
import { useKitchen } from "./-lib/use-kitchen";
import { usePaging } from "./-lib/use-paging";
import { SearchField } from "@/components/app/patterns/search-field";

export const Route = createFileRoute("/_authenticated/menus/dishes")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { q?: string; meal?: MenuMeal; source?: "library" | "kitchen" } => ({
    q: optionalSearch(search.q),
    meal: optionalOneOf(MENU_MEALS, search.meal),
    source: optionalOneOf(["library", "kitchen"] as const, search.source),
  }),
  component: DishesPage,
});

/** The dishes a kitchen can serve: the platform's curated library and its own. */
function DishesPage() {
  const { facilityId, canManage } = useKitchen();
  const { q, meal: pickedMeal, source: pickedSource } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = searchPatch(navigate);
  const {
    text: search,
    change: setSearch,
    settling,
  } = useSearchParamText(q, (next) => patch({ q: next }));
  const meal: MenuMeal | "" = pickedMeal ?? "";
  const setMeal = (next: MenuMeal | "") => patch({ meal: next || undefined });
  const source: "library" | "kitchen" | "" = pickedSource ?? "";
  const setSource = (next: "library" | "kitchen" | "") => patch({ source: next || undefined });
  const paging = usePaging();
  const [open, setOpen] = useState<SheetTarget | null>(null);
  const dishes = useQuery({
    ...orpc.agents.menus.dishes.list.queryOptions({
      input: {
        facilityId,
        limit: paging.size,
        offset: paging.offset,
        ...(q ? { search: q } : {}),
        ...(meal ? { meal } : {}),
        ...(source ? { source } : {}),
      },
    }),
    placeholderData: keepWhileSame({ facilityId }),
  });
  const searching = settling || dishes.isPlaceholderData;
  const reset = paging.reset;

  return (
    <section className="space-y-4">
      <CommercialKitchenBar facilityId={facilityId} canManage={canManage} />
      <div className="flex flex-wrap items-center gap-2">
        <SearchField
          value={search}
          onChange={(value) => {
            setSearch(value);
            reset();
          }}
          placeholder="Search dishes"
          className="min-w-56 flex-1"
        />
        <OptionSelect
          value={meal}
          aria-label="Meal"
          onChange={(event) => {
            setMeal(event.target.value as MenuMeal | "");
            reset();
          }}
        >
          <SelectOption value="">Every meal</SelectOption>
          {MENU_MEALS.map((known) => (
            <SelectOption key={known} value={known}>
              {MENU_MEAL_LABELS[known]}
            </SelectOption>
          ))}
        </OptionSelect>
        <OptionSelect
          value={source}
          aria-label="Source"
          onChange={(event) => {
            setSource(event.target.value as "library" | "kitchen" | "");
            reset();
          }}
        >
          <SelectOption value="">Library and ours</SelectOption>
          <SelectOption value="library">Library</SelectOption>
          <SelectOption value="kitchen">Our kitchen</SelectOption>
        </OptionSelect>
        {canManage ? (
          <Button onClick={() => setOpen({ kind: "new" })}>
            <Plus className="size-4" />
            New dish
          </Button>
        ) : null}
      </div>

      {dishes.isPending ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : dishes.isError ? (
        <ReadFailed
          message="Couldn't load the dishes."
          error={dishes.error}
          onRetry={() => void dishes.refetch()}
        />
      ) : dishes.data.rows.length === 0 && searching ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : dishes.data.rows.length === 0 ? (
        search.trim() || meal || source ? (
          <EmptyState
            title="No dish matches these filters"
            description="Try another word, or show every meal from the library and your kitchen."
            action={
              <Button
                variant="outline"
                onClick={() => {
                  void navigate({ search: {}, ...KEEP_PLACE });
                  reset();
                }}
              >
                Clear filters
              </Button>
            }
          />
        ) : (
          <EmptyState
            title="No dishes yet"
            description="Dishes are what the calendar plans with: a name, a portion and what goes into it."
          />
        )
      ) : (
        <div
          aria-busy={searching || undefined}
          className={cn(
            "bg-card shadow-card divide-y overflow-hidden rounded-xl border transition-opacity",
            searching && "opacity-60",
          )}
        >
          <ul className="divide-y">
            {dishes.data.rows.map((dish) => (
              <li key={dish.id}>
                <button
                  type="button"
                  onClick={() => setOpen({ kind: "dish", dishId: dish.id })}
                  className="hover:bg-accent focus-visible:ring-ring/80 grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-4 py-2.5 text-left outline-hidden transition-colors focus-visible:ring-[3px] focus-visible:ring-inset"
                >
                  <span className="flex min-w-0 items-start gap-2.5">
                    <CourseIcon course={dish.course} className="mt-0.5 size-4" />
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium">{dish.name}</span>
                      <span className="text-muted-foreground block text-xs text-pretty">
                        {[
                          dish.meal ? MENU_MEAL_LABELS[dish.meal] : null,
                          MENU_COURSE_LABELS[dish.course],
                          dish.portion,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                        {/* Allergens in the amber the calendar's mark uses, so they read as the
                          safety fact they are rather than one more grey detail. */}
                        {dish.allergens.length > 0 ? (
                          <>
                            {", "}
                            <span className="text-warn">
                              Contains{" "}
                              {dish.allergens
                                .map((allergen) => MENU_ALLERGEN_LABELS[allergen])
                                .join(", ")}
                            </span>
                          </>
                        ) : null}
                      </span>
                    </span>
                  </span>
                  <span
                    className={
                      dish.source === "library"
                        ? "text-faint shrink-0 text-xs"
                        : "text-primary-ink shrink-0 text-xs font-medium"
                    }
                  >
                    {dish.source === "library" ? "Library" : "Our kitchen"}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <MenusPager paging={paging} total={dishes.data.total} noun={["dish", "dishes"]} />
        </div>
      )}

      {open ? (
        <DishSheet
          facilityId={facilityId}
          target={open}
          canManage={canManage}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </section>
  );
}
